import { ReactNode } from 'react';
import { useTheme } from '@mui/material/styles';
import { IAbsenteeismRateItem } from '../../../interfaces/IAbsenteeismRate';
import { chartColorsFor, readableTextOn } from './chartColors';
import {
  Amount,
  Card,
  CardHeader,
  CardHeading,
  CardIcon,
  CardSubtitle,
  CardTitle,
  Fill,
  ItemLabel,
  List,
  Percentage,
  PercentageSign,
  Row,
  Track,
  Value,
  ValueLayer,
} from './AbsenteeismRateCard.styles';

interface IAbsenteeismRateCardTitleProps {
  icon: ReactNode;
  title: string;
  subtitle: string;
}

export const AbsenteeismRateCardTitle = ({ icon, title, subtitle }: IAbsenteeismRateCardTitleProps) => (
  <CardHeader>
    <CardIcon aria-hidden="true">{icon}</CardIcon>
    <CardHeading>
      <CardTitle>{title}</CardTitle>
      <CardSubtitle>{subtitle}</CardSubtitle>
    </CardHeading>
  </CardHeader>
);

interface IAbsenteeismRateCardProps {
  icon: ReactNode;
  title: string;
  subtitle: string;
  data: IAbsenteeismRateItem[];
}

export const AbsenteeismRateCard = ({ icon, title, subtitle, data }: IAbsenteeismRateCardProps) => {
  const { palette } = useTheme();
  const colors = chartColorsFor(palette.mode === 'dark');

  return (
    <Card $isDark={palette.mode === 'dark'}>
      <AbsenteeismRateCardTitle icon={icon} title={title} subtitle={subtitle} />

      <List>
        {data.map(({ label, withCertificate, total, percentage }, index) => {
          const amountLabel = `${withCertificate} de ${total}`;
          const barColor = colors[index % colors.length];
          const width = Math.min(Math.max(percentage, 0), 100);

          const value = (
            <Value>
              <Percentage>
                {percentage}
                <PercentageSign>%</PercentageSign>
              </Percentage>
              <Amount>{amountLabel}</Amount>
            </Value>
          );

          return (
            <Row key={label}>
              <ItemLabel>{label}</ItemLabel>

              <Track role="img" aria-label={`${label}: ${percentage}%, ${amountLabel} colaboradores`}>
                <ValueLayer>{value}</ValueLayer>
                <Fill
                  aria-hidden="true"
                  style={{
                    background: barColor,
                    color: readableTextOn(barColor),
                    clipPath: `inset(0 ${100 - width}% 0 0 round 11px)`,
                  }}
                >
                  {value}
                </Fill>
              </Track>
            </Row>
          );
        })}
      </List>
    </Card>
  );
};
